// AttachmentSection — shared add / list / view (lightbox) / delete UI
// for skip-job and skip-enquiry attachments. Both surfaces share the
// same DTO shape so the same component drives both pages; the kind
// prop discriminates which set of hooks (job vs enquiry) to use.
//
// Layout:
//   [+ Upload]      (file input — accepts JPEG / PNG / WebP / PDF)
//   [tile grid]     (image thumbnails inline; PDF tiles show a label)
//      ↳ click an image opens a lightbox (modal) with the full-size
//        rendering and a "Open in new tab" link.
//      ↳ click a PDF opens the presigned URL in a new tab.
//      ↳ each tile has a small × that fires a confirm popup.
//
// Behaviour notes:
//   - File input uses `accept` to nudge the picker; final mime
//     validation is server-side (magic-byte sniffer).
//   - Multiple files in a single picker action are uploaded in one
//     POST (the multer `.array('attachments', N)` route accepts
//     up to MAX_ATTACHMENTS_PER_(JOB|ENQUIRY)).
//   - Read-only mode is supported via `readOnly` prop — used on
//     detail pages where the user shouldn't add/remove (e.g.
//     terminal-status jobs).

import { useRef, useState } from 'react';
import { Loader2, Paperclip, Trash2, X } from 'lucide-react';

import { Button } from '../../../components/ui/Button/Button';
import { Modal } from '../../../components/ui/Modal/Modal';
import {
  useDeleteSkipEnquiryAttachment,
  useDeleteSkipJobAttachment,
  useSkipEnquiryAttachments,
  useSkipJobAttachments,
  useUploadSkipEnquiryAttachments,
  useUploadSkipJobAttachments,
} from '../hooks/useSkipAttachments';
import { extractErrorMessage } from '../../../utils/errorMessage';
import type {
  SkipEnquiryAttachment,
  SkipJobAttachment,
} from '../../../types/api';
import styles from './AttachmentSection.module.css';

export interface AttachmentSectionProps {
  kind: 'job' | 'enquiry';
  encId: string;
  /** When true, hides the upload control + per-tile delete button.
   *  Used on terminal jobs / converted enquiries where mutations
   *  should be blocked. */
  readOnly?: boolean;
}

type AnyAttachment = SkipJobAttachment | SkipEnquiryAttachment;

const ACCEPT_MIMES = 'image/jpeg,image/png,image/webp,application/pdf';

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(mime: string): boolean {
  return mime.startsWith('image/');
}

export function AttachmentSection({
  kind,
  encId,
  readOnly = false,
}: AttachmentSectionProps): JSX.Element {
  // Choose the right pair of hooks. Both kinds expose the same DTO
  // shape so the rest of the component is fully shared.
  const jobsList = useSkipJobAttachments(kind === 'job' ? encId : null);
  const enqList = useSkipEnquiryAttachments(kind === 'enquiry' ? encId : null);
  const list = kind === 'job' ? jobsList : enqList;

  // The two upload / delete hooks are wired unconditionally for type
  // simplicity; only one's mutate is ever called per instance.
  const uploadJob = useUploadSkipJobAttachments(kind === 'job' ? encId : '');
  const uploadEnq = useUploadSkipEnquiryAttachments(kind === 'enquiry' ? encId : '');
  const upload = kind === 'job' ? uploadJob : uploadEnq;

  const deleteJob = useDeleteSkipJobAttachment(kind === 'job' ? encId : '');
  const deleteEnq = useDeleteSkipEnquiryAttachment(kind === 'enquiry' ? encId : '');
  const del = kind === 'job' ? deleteJob : deleteEnq;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<AnyAttachment | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AnyAttachment | null>(null);

  function handleUploadClick() {
    fileInputRef.current?.click();
  }

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setError(null);
    upload.mutate(files, {
      onError: (err) => setError(extractErrorMessage(err)),
      onSettled: () => {
        // Reset the input so the same file can be re-picked.
        if (fileInputRef.current) fileInputRef.current.value = '';
      },
    });
  }

  function handleConfirmDelete() {
    if (!confirmDelete) return;
    setError(null);
    del.mutate(
      { attachmentEncId: confirmDelete.enc_id },
      {
        onSuccess: () => setConfirmDelete(null),
        onError: (err) => setError(extractErrorMessage(err)),
      },
    );
  }

  function handleTileClick(att: AnyAttachment) {
    if (isImage(att.file_type)) {
      setLightbox(att);
    } else if (att.presigned_url) {
      window.open(att.presigned_url, '_blank', 'noopener,noreferrer');
    }
  }

  const items: AnyAttachment[] = list.data?.attachments ?? [];

  return (
    <div className={styles.root}>
      {!readOnly ? (
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            icon={Paperclip}
            onClick={handleUploadClick}
            disabled={upload.isPending}
          >
            {upload.isPending ? 'Uploading…' : 'Add attachments'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_MIMES}
            className={styles.hiddenInput}
            onChange={(e) => handleFiles(e.target.files)}
          />
          <span className={styles.hint}>
            JPEG, PNG, WebP or PDF — up to a few MB each.
          </span>
        </div>
      ) : null}

      {error ? <p className={styles.error}>{error}</p> : null}

      {list.isLoading ? (
        <p className={styles.empty}>
          <Loader2 size={14} className={styles.spinner} aria-hidden /> Loading…
        </p>
      ) : items.length === 0 ? (
        <p className={styles.empty}>No attachments yet.</p>
      ) : (
        <ul className={styles.grid}>
          {items.map((att) => {
            const img = isImage(att.file_type);
            return (
              <li key={att.enc_id} className={styles.tile}>
                <button
                  type="button"
                  className={styles.tileButton}
                  onClick={() => handleTileClick(att)}
                  title={att.original_filename}
                >
                  {img && att.presigned_url ? (
                    <img
                      src={att.presigned_url}
                      alt={att.original_filename}
                      className={styles.thumb}
                      loading="lazy"
                    />
                  ) : (
                    <span className={styles.pdfIcon} aria-hidden>
                      PDF
                    </span>
                  )}
                  <span className={styles.tileMeta}>
                    <span className={styles.filename}>{att.original_filename}</span>
                    <span className={styles.filesize}>{fmtBytes(att.file_size)}</span>
                  </span>
                </button>
                {!readOnly ? (
                  <button
                    type="button"
                    className={styles.tileDelete}
                    aria-label={`Delete ${att.original_filename}`}
                    onClick={() => setConfirmDelete(att)}
                    disabled={del.isPending && confirmDelete?.enc_id === att.enc_id}
                  >
                    <Trash2 size={14} />
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {/* Lightbox — full-size image rendering. PDFs open in a new
          tab from handleTileClick instead. */}
      {lightbox ? (
        <Modal
          open
          onClose={() => setLightbox(null)}
          title={lightbox.original_filename}
          size="lg"
          footer={
            <div className={styles.lightboxFooter}>
              {lightbox.presigned_url ? (
                <a
                  href={lightbox.presigned_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  className={styles.openInTab}
                >
                  Open in new tab
                </a>
              ) : null}
              <Button variant="secondary" onClick={() => setLightbox(null)}>
                Close
              </Button>
            </div>
          }
        >
          {lightbox.presigned_url ? (
            <img
              src={lightbox.presigned_url}
              alt={lightbox.original_filename}
              className={styles.lightboxImg}
            />
          ) : (
            <p>Image preview is not available — link expired.</p>
          )}
        </Modal>
      ) : null}

      {/* Confirm-delete popup. Soft-delete on the server keeps the
          MinIO object recoverable, but the UI presents this as a
          destructive action. */}
      {confirmDelete ? (
        <Modal
          open
          onClose={() => (del.isPending ? undefined : setConfirmDelete(null))}
          title="Delete attachment?"
          size="sm"
          footer={
            <div className={styles.confirmFooter}>
              <Button
                variant="ghost"
                onClick={() => setConfirmDelete(null)}
                disabled={del.isPending}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                icon={X}
                onClick={handleConfirmDelete}
                loading={del.isPending}
              >
                Delete
              </Button>
            </div>
          }
        >
          <p>
            Remove <strong>{confirmDelete.original_filename}</strong>? The file
            will no longer appear on this {kind === 'job' ? 'job' : 'enquiry'}.
          </p>
        </Modal>
      ) : null}
    </div>
  );
}
